// Global booking search (spec §6.2: "Global booking search by
// reference/guest/host"), backed by admin-bookings' `search` kind
// (admin_booking_search SQL RPC).
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction } from '@/lib/api/edgeFunction';

export const Route = createFileRoute('/bookings/')({
  component: BookingsSearch,
});

interface SearchResultRow {
  id: string;
  reference_number: string | null;
  status: string;
  check_in: string | null;
  business_name?: string | null;
  guest_name?: string | null;
  total_price?: number | null;
  [key: string]: unknown;
}

const STATUS_OPTIONS = [
  '', 'pending', 'confirmed', 'checked_in', 'completed', 'cancelled', 'expired',
];

function useBookingSearch(q: string, status: string) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-bookings', 'search', q, status],
    queryFn: () =>
      callEdgeFunction<{ results: SearchResultRow[]; total: number }>(supabase, 'admin-bookings', {
        q: q || undefined,
        status: status || undefined,
        limit: 50,
        offset: 0,
      }),
    // Minor fix, round 1: don't search on mount with an empty query and no
    // status — that used to run immediately, and a genuine zero-result
    // search was indistinguishable from "haven't searched yet" (neither the
    // prompt nor a table rendered).
    enabled: q !== '' || status !== '',
  });
}

function BookingsSearch() {
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('');
  const [committed, setCommitted] = useState('');

  const { data, isLoading, error } = useBookingSearch(committed, status);
  const results = data?.results ?? [];

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Bookings &amp; money</h1>
        <p className="mt-sm text-body text-ink-2">Search by reference, guest name/email or host name.</p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setCommitted(input.trim());
        }}
        className="flex flex-wrap items-center gap-sm"
      >
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Reference, guest or host…"
          className="min-w-[240px] flex-1 rounded-button border border-border bg-surface px-md py-sm text-body text-ink"
        />
        <select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-button border border-border bg-surface px-md py-sm text-body text-ink"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s || 'Any status'}
            </option>
          ))}
        </select>
        <button type="submit" className="rounded-button bg-canyon px-md py-sm text-body font-semibold text-white">
          Search
        </button>
      </form>

      {isLoading ? <p className="text-body text-ink-2">Searching…</p> : null}
      {error ? <p className="text-body text-danger">{(error as Error).message}</p> : null}

      {!isLoading && !error && committed === '' && !status ? (
        <p className="rounded-card border border-border bg-surface p-xl text-center text-body text-ink-2">
          Enter a reference, guest name/email or host name to search.
        </p>
      ) : null}

      {!isLoading && !error && (committed !== '' || status !== '') && results.length === 0 ? (
        <p className="rounded-card border border-border bg-surface p-xl text-center text-body text-ink-2">
          No bookings match.
        </p>
      ) : null}

      {results.length > 0 ? (
        <div className="overflow-x-auto rounded-card border border-border bg-surface shadow-card">
          <table className="w-full min-w-[760px] text-body text-ink">
            <thead>
              <tr className="border-b border-hair text-left text-caption text-ink-2">
                <th className="px-md py-sm font-semibold">Reference</th>
                <th className="px-md py-sm font-semibold">Guest</th>
                <th className="px-md py-sm font-semibold">Business</th>
                <th className="px-md py-sm font-semibold">Status</th>
                <th className="px-md py-sm font-semibold">Check-in</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.id} className="border-b border-hair last:border-0 hover:bg-surface-2">
                  <td className="px-md py-sm">
                    <Link to="/bookings/$id" params={{ id: r.id }} className="font-semibold text-canyon underline">
                      {r.reference_number ?? r.id}
                    </Link>
                  </td>
                  <td className="px-md py-sm">{r.guest_name ?? '—'}</td>
                  <td className="px-md py-sm">{r.business_name ?? '—'}</td>
                  <td className="px-md py-sm">{r.status}</td>
                  <td className="px-md py-sm">{r.check_in ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
